import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import GuidedTour from '@/components/GuidedTour';

/**
 * TourManager — v6.32.163
 *
 * Auto-opens the GuidedTour exactly once per user (localStorage sentinel
 * `lerno_tour_<role>`) AND at most once per browser session
 * (`sessionStorage` belt-and-suspenders so route changes within the
 * session can never re-trigger it). Manual replay via the
 * `replay-tour` window event is still supported and explicitly bypasses
 * both sentinels.
 */
const MAIN_PAGES = ['/', '/grades', '/teacher', '/admin/district', '/admin/school', '/superadmin'];
const SESSION_KEY = 'mosaic_tour_session_shown';

const roleKey = (role) => {
  if (['district_admin', 'school_admin', 'super_admin'].includes(role)) return 'admin';
  if (role === 'teacher') return 'teacher';
  return 'student';
};

const TourManager = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [showTour, setShowTour] = React.useState(false);

  React.useEffect(() => {
    if (loading || !isAuthenticated || !user) return;
    const rkey = roleKey(user.role);
    const localCompleted = localStorage.getItem(`lerno_tour_${rkey}`);
    const sessionShown = sessionStorage.getItem(SESSION_KEY);
    const isMainPage = MAIN_PAGES.includes(location.pathname);

    if (!localCompleted && !sessionShown && isMainPage) {
      // Mark BOTH sentinels immediately so route changes / refresh
      // / accidental remounts can never re-trigger this in the same
      // browser session. Permanent dismissal is also written so
      // future browser sessions skip the tour by default.
      sessionStorage.setItem(SESSION_KEY, '1');
      localStorage.setItem(`lerno_tour_${rkey}`, 'completed');
      const t = setTimeout(() => setShowTour(true), 800);
      return () => clearTimeout(t);
    }
  }, [user, isAuthenticated, loading, location.pathname]);

  React.useEffect(() => {
    // Manual replay channel — bypasses both sentinels.
    const handler = () => setShowTour(true);
    window.addEventListener('replay-tour', handler);
    return () => window.removeEventListener('replay-tour', handler);
  }, []);

  if (!showTour || !user) return null;
  return <GuidedTour role={user.role} onComplete={() => setShowTour(false)} />;
};

export default TourManager;
